// Themed Button primitive. Strawberry-gradient `default` variant for
// the primary action; subtle pastel tints for secondary, ghost, and
// destructive variants.

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "ui-glass-button app-primary-action bg-strawberry-gradient text-primary-foreground shadow-sm shadow-strawberry-200/40 hover:opacity-95 hover:shadow-md hover:shadow-strawberry-200/60",
        secondary:
          "ui-glass-button bg-strawberry-50 text-strawberry-700 hover:bg-strawberry-100 border border-strawberry-100",
        outline:
          "ui-glass-button border border-strawberry-200 bg-white/70 backdrop-blur-sm text-foreground hover:bg-strawberry-50 hover:text-strawberry-700",
        ghost:
          "ui-glass-ghost text-foreground/80 hover:bg-strawberry-50 hover:text-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link: "text-strawberry-600 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-11 rounded-lg px-6 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
